/**
 * PropertySearchBar - Core Reusable Property Search Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides property search functionality with customizable fields and behavior.
 * Themes can control styling, visible fields, and sticky behavior.
 */

'use client';

import { useState } from 'react';
import { GradientButton } from '../ui/GradientButton';

export interface PropertySearchValues {
  location: string;
  type: string;
  priceRange: string;
  bedrooms: string;
}

export interface PropertySearchBarProps {
  /**
   * Callback when search is submitted
   */
  onSearch?: (values: PropertySearchValues) => void;
  
  /**
   * Custom className for the container
   */
  className?: string;
  
  /**
   * Fields to show
   */
  fields?: {
    location?: boolean;
    type?: boolean;
    price?: boolean;
    bedrooms?: boolean;
  };
  
  /**
   * Custom labels
   */
  labels?: {
    location?: string;
    type?: string;
    price?: string;
    bedrooms?: string;
    searchButton?: string;
  };
  
  /**
   * Initial values
   */
  initialValues?: Partial<PropertySearchValues>;
  
  /**
   * Render prop for custom wrapper
   */
  renderWrapper?: (props: { children: React.ReactNode; isFocused: boolean }) => React.ReactNode;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Core Property Search Bar Component
 * 
 * Provides the search form structure with semantic HTML.
 * Themes customize appearance via className and renderWrapper.
 */
export function PropertySearchBar({
  onSearch,
  className = '',
  fields = {
    location: true,
    type: true,
    price: true,
    bedrooms: true,
  },
  labels = {},
  initialValues = {},
  renderWrapper,
  messages,
  locale: _locale = 'en',
}: PropertySearchBarProps) {
  const [values, setValues] = useState<PropertySearchValues>({
    location: initialValues.location || '',
    type: initialValues.type || '',
    priceRange: initialValues.priceRange || '',
    bedrooms: initialValues.bedrooms || '',
  });

  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(values);
  };

  const handleChange = (field: keyof PropertySearchValues, value: string) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  // Get translated option texts
  const propertySearch = messages?.propertySearch as Record<string, unknown> | undefined;
  const options = propertySearch?.options as Record<string, unknown> | undefined;
  const propertyTypes = options?.propertyTypes as Record<string, unknown> | undefined;
  const priceRanges = options?.priceRanges as Record<string, unknown> | undefined;
  const bedroomCounts = options?.bedroomCounts as Record<string, unknown> | undefined;
  
  const t = {
    all: safeGetString(options?.all, 'All'),
    any: safeGetString(options?.any, 'Any'),
    propertyTypes: {
      house: safeGetString(propertyTypes?.house, 'House'),
      apartment: safeGetString(propertyTypes?.apartment, 'Apartment'),
      villa: safeGetString(propertyTypes?.villa, 'Villa'),
      penthouse: safeGetString(propertyTypes?.penthouse, 'Penthouse'),
      loft: safeGetString(propertyTypes?.loft, 'Loft'),
    },
    priceRanges: {
      under500k: safeGetString(priceRanges?.under500k, 'Under $500K'),
      '500kTo1m': safeGetString(priceRanges?.['500kTo1m'], '$500K - $1M'),
      '1mTo2m': safeGetString(priceRanges?.['1mTo2m'], '$1M - $2M'),
      over2m: safeGetString(priceRanges?.over2m, '$2M+'),
    },
    bedroomCounts: {
      '1plus': safeGetString(bedroomCounts?.['1plus'], '1+'),
      '2plus': safeGetString(bedroomCounts?.['2plus'], '2+'),
      '3plus': safeGetString(bedroomCounts?.['3plus'], '3+'),
      '4plus': safeGetString(bedroomCounts?.['4plus'], '4+'),
      '5plus': safeGetString(bedroomCounts?.['5plus'], '5+'),
    },
  };

  const searchForm = (
    <form onSubmit={handleSubmit} className={`property-search-bar ${className}`}>
      <div className="property-search-fields">
        {/* Location Field */}
        {fields.location && (
          <div className="property-search-field" data-field="location">
            <label className="property-search-label">
              {labels.location || 'Location'}
            </label>
            <div className="property-search-input-wrapper">
              <svg className="property-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="text"
                value={values.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder={labels.location || 'Search locations'}
                className="property-search-input"
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
              />
            </div>
          </div>
        )}

        {/* Type Field */}
        {fields.type && (
          <div className="property-search-field property-search-field-select" data-field="type">
            <label htmlFor="property-type-select" className="property-search-label">
              {labels.type || 'Type'}
            </label>
            <select
              id="property-type-select"
              value={values.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="property-search-select"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <option value="">{t.all}</option>
              <option value="house">{t.propertyTypes.house}</option>
              <option value="apartment">{t.propertyTypes.apartment}</option>
              <option value="villa">{t.propertyTypes.villa}</option>
              <option value="penthouse">{t.propertyTypes.penthouse}</option>
              <option value="loft">{t.propertyTypes.loft}</option>
            </select>
            <svg className="property-search-dropdown-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}

        {/* Price Field */}
        {fields.price && (
          <div className="property-search-field property-search-field-select" data-field="price">
            <label htmlFor="property-price-select" className="property-search-label">
              {labels.price || 'Price'}
            </label>
            <select
              id="property-price-select"
              value={values.priceRange}
              onChange={(e) => handleChange('priceRange', e.target.value)}
              className="property-search-select"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <option value="">{t.any}</option>
              <option value="0-500000">{t.priceRanges.under500k}</option>
              <option value="500000-1000000">{t.priceRanges['500kTo1m']}</option>
              <option value="1000000-2000000">{t.priceRanges['1mTo2m']}</option>
              <option value="2000000+">{t.priceRanges.over2m}</option>
            </select>
            <svg className="property-search-dropdown-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}

        {/* Bedrooms Field */}
        {fields.bedrooms && (
          <div className="property-search-field property-search-field-select" data-field="bedrooms">
            <label htmlFor="property-bedrooms-select" className="property-search-label">
              {labels.bedrooms || 'Beds'}
            </label>
            <select
              id="property-bedrooms-select"
              value={values.bedrooms}
              onChange={(e) => handleChange('bedrooms', e.target.value)}
              className="property-search-select"
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <option value="">{t.any}</option>
              <option value="1">{t.bedroomCounts['1plus']}</option>
              <option value="2">{t.bedroomCounts['2plus']}</option>
              <option value="3">{t.bedroomCounts['3plus']}</option>
              <option value="4">{t.bedroomCounts['4plus']}</option>
              <option value="5">{t.bedroomCounts['5plus']}</option>
            </select>
            <svg className="property-search-dropdown-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}

        {/* Search Button */}
        <div className="property-search-button-container">
          <GradientButton
            type="submit"
            className="property-search-button"
            ariaLabel="Search properties"
            shine={true}
            hoverScale={true}
            icon={
              <svg className="property-search-button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
            iconPosition="left"
          >
            <span className="property-search-button-text">
              {labels.searchButton || 'Search'}
            </span>
          </GradientButton>
        </div>
      </div>
    </form>
  );

  // Use custom wrapper if provided
  if (renderWrapper) {
    return <>{renderWrapper({ children: searchForm, isFocused })}</>;
  }

  return searchForm;
}

